import { useApplicationConfig } from '~/data/applicationConfig';
import { ProposalType } from '~/data/proposals';
import {
  UseEmployeeProposalsDataOutput,
  useEmploymentProposalsData,
} from '~/features/ScheduledEmploymentChanges/Common/EmploymentProposals/useEmploymentProposalsData';
import {
  UseEmploymentProposalsPreviewData,
  useEmploymentProposalsPreviewData,
} from '~/features/ScheduledEmploymentChanges/Common/EmploymentProposals/useEmploymentProposalsPreviewData';

type PropsType = {
  employeeId: number;
};

export type EmploymentProposalsDataAndConfig = Pick<
  UseEmployeeProposalsDataOutput,
  'details' | 'positions'
> &
  Pick<
    UseEmploymentProposalsPreviewData,
    'detailsPreview' | 'positionPreview'
  > & {
    config: {
      proposalsEnabled: boolean;
      workflowsEngineEnabled: boolean;
      employmentProposalsConfigured: boolean;
    };
    isLoading: boolean;
  };

export const useEmploymentProposals = (
  props: PropsType,
): EmploymentProposalsDataAndConfig => {
  const { employeeId } = props;

  const { applicationConfig } = useApplicationConfig();
  const isNewEngineEnabled =
    applicationConfig.fComConfig.miscellaneousConfig
      .fComEnableNewWorkflowsEngine;

  const requestProps = {
    details: {
      targetId: employeeId,
      proposalType: ProposalType.EMPLOYMENT_DETAILS_PROPOSAL,
    },
    positions: {
      targetId: employeeId,
      proposalType: ProposalType.POSITION_INTERNAL_PROPOSAL,
    },
  };

  const {
    isLoading: isLoadingPreviews,
    hasData: hasPreviewConfigs,
    ...previewsRest
  } = useEmploymentProposalsPreviewData(requestProps);

  const { isLoading: isLoadingProposals, ...proposalsRest } =
    useEmploymentProposalsData(requestProps);

  return {
    ...previewsRest,
    ...proposalsRest,
    config: {
      proposalsEnabled: isNewEngineEnabled && hasPreviewConfigs,
      workflowsEngineEnabled: isNewEngineEnabled,
      employmentProposalsConfigured: hasPreviewConfigs,
    },
    isLoading: isLoadingPreviews || isLoadingProposals,
  };
};
